import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TreePine, Heart, Users, Clock, BookOpen, Shield, Share2, Mic, MessageCircle, ArrowRight, ChevronDown, User, Settings, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function LandingPage({ user }) {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);

  const handleLogin = (redirectPath = '/home') => {
    sessionStorage.setItem('authRedirectPath', redirectPath);
    const redirectUrl = window.location.origin + '/home';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <TreePine className="w-8 h-8 text-primary" strokeWidth={1.5} />
            <h1 className="text-2xl font-serif font-semibold text-foreground">Sarmoh</h1>
          </div>
          
          {/* Center Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Button 
              variant="ghost"
              onClick={() => navigate('/memorial-tribute')}
              className="text-foreground-muted hover:text-primary hover:bg-primary/5 rounded-full px-5 py-2 font-sans text-sm"
            >
              <Heart className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Memorial Tribute
            </Button>
            <Button 
              variant="ghost"
              onClick={() => navigate('/family-tree')}
              className="text-foreground-muted hover:text-primary hover:bg-primary/5 rounded-full px-5 py-2 font-sans text-sm"
            >
              <TreePine className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Family Tree
            </Button>
          </div>

          {/* Right Side - Auth Buttons or User Menu */}
          <div className="flex items-center space-x-3">
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                  )}
                  <span className="text-sm font-sans text-foreground hidden sm:block">{user.name}</span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-border rounded-lg shadow-lg py-1 z-50">
                    <button
                      onClick={() => { setShowUserMenu(false); navigate('/settings'); }}
                      className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-gray-50 flex items-center"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Account Settings
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Button 
                  variant="ghost"
                  onClick={() => handleLogin('/home')}
                  className="text-foreground-muted hover:text-primary rounded-full px-5 py-2 font-sans text-sm"
                >
                  Sign Up
                </Button>
                <Button 
                  onClick={() => handleLogin('/home')}
                  data-testid="landing-login-btn"
                  className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-6 py-2 font-sans text-sm shadow-md transition-all hover:scale-[1.02]"
                >
                  Sign In
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl font-serif tracking-tight text-foreground mb-6">
              Preserve Memories.<br />Honor Legacies.
            </h1>
            <p className="text-lg md:text-xl font-sans text-foreground-muted leading-relaxed mb-12 max-w-3xl mx-auto">
              Sarmoh helps families stay connected across generations — whether you're building your family tree or creating a beautiful tribute for a loved one.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Two Main CTAs */}
      <section className="pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Memorial Tribute Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="group bg-white border border-border shadow-card rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary/30 transition-all"
            >
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 p-8 border-b border-border group-hover:from-primary/15 group-hover:to-primary/10 transition-colors">
                <Heart className="w-14 h-14 text-primary mb-4" strokeWidth={1.5} />
                <h2 className="text-3xl font-serif text-foreground mb-3">Memorial Tribute</h2>
                <p className="text-base font-sans text-foreground-muted leading-relaxed">
                  Create a beautiful tribute page for a loved one. Collect messages, voice recordings, and memories from family and friends.
                </p>
              </div>
              <div className="p-8">
                <ul className="space-y-3 mb-8">
                  {[
                    'Create a shareable tribute page',
                    'Collect text & voice messages',
                    'Support for Bhog, Ardas, Tehrvi & more',
                    'Generate shareable memorial cards',
                    'View word cloud of memories'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-start space-x-3 text-sm font-sans text-foreground-muted">
                      <Heart className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => navigate('/memorial-tribute')}
                  data-testid="cta-memorial-btn"
                  variant="outline"
                  className="w-full border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full py-4 font-serif tracking-wide transition-all hover:scale-[1.01]"
                >
                  Create a Memorial Tribute
                  <ArrowRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
                </Button>
              </div>
            </motion.div>

            {/* Family Tree Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="group bg-white border border-border shadow-card rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary/30 transition-all"
            >
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 p-8 border-b border-border group-hover:from-emerald-100 group-hover:to-emerald-50 transition-colors">
                <TreePine className="w-14 h-14 text-primary mb-4" strokeWidth={1.5} />
                <h2 className="text-3xl font-serif text-foreground mb-3">Family Tree</h2>
                <p className="text-base font-sans text-foreground-muted leading-relaxed">
                  Build an interactive family tree. Add members, track relationships, and preserve your family history for generations.
                </p>
              </div>
              <div className="p-8">
                <ul className="space-y-3 mb-8">
                  {[
                    'Interactive tree visualization',
                    'Add photos, bios & locations',
                    'Timeline of family events',
                    'Collaborative editing with approval',
                    'Export & share your tree'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-start space-x-3 text-sm font-sans text-foreground-muted">
                      <TreePine className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => navigate('/family-tree')}
                  data-testid="cta-family-tree-btn"
                  variant="outline"
                  className="w-full border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full py-4 font-serif tracking-wide transition-all hover:scale-[1.01]"
                >
                  Build Your Family Tree
                  <ArrowRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Family Photo Section */}
      <section className="py-16 px-6 bg-paper">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_kinship-board/artifacts/5bdumhzw_fmily.jpg"
                alt="Multi-generational family"
                className="rounded-2xl shadow-floating w-full"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-serif tracking-tight text-foreground mb-6">
                Every Family Has a Story Worth Preserving
              </h2>
              <p className="text-base font-sans text-foreground-muted leading-relaxed mb-6">
                From celebrating milestones to honoring those who've passed, Sarmoh gives you the tools to keep your family's legacy alive. Whether it's a memorial service like <strong>Bhog and Ardas</strong> or <strong>Tehrvi</strong>, or simply documenting your family tree — we're here to help.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 bg-white border border-border rounded-full text-sm font-sans text-foreground-muted">Memorial Services</span>
                <span className="px-4 py-2 bg-white border border-border rounded-full text-sm font-sans text-foreground-muted">Family Trees</span>
                <span className="px-4 py-2 bg-white border border-border rounded-full text-sm font-sans text-foreground-muted">Voice Tributes</span>
                <span className="px-4 py-2 bg-white border border-border rounded-full text-sm font-sans text-foreground-muted">Timelines</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif tracking-tight text-foreground mb-4">
              Built for Families, By Heart
            </h2>
            <p className="text-base font-sans text-foreground-muted max-w-2xl mx-auto">
              Simple, thoughtful features designed to help you preserve what matters most.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Share2,
                title: "Easy Sharing",
                description: "Generate unique links and beautiful shareable images for WhatsApp, email, or any platform."
              },
              {
                icon: Mic,
                title: "Voice Messages",
                description: "Let loved ones record heartfelt voice tributes that capture emotion words can't express."
              },
              {
                icon: Shield,
                title: "Family Control",
                description: "One family point-of-contact manages edits and approvals to keep information accurate."
              },
              {
                icon: Users,
                title: "Guest Contributions",
                description: "Anyone can add tributes without creating an account — anonymous option available."
              },
              {
                icon: Clock,
                title: "Timeline Events",
                description: "Chronicle important family milestones, celebrations, and moments that shaped your story."
              },
              {
                icon: BookOpen,
                title: "Collaborative Stories",
                description: "Multiple family members can contribute memories and stories together."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-border shadow-card rounded-xl p-6 hover:shadow-md hover:border-primary/30 transition-all"
              >
                <feature.icon className="w-10 h-10 text-primary mb-4" strokeWidth={1.5} />
                <h3 className="text-xl font-serif text-foreground mb-2">{feature.title}</h3>
                <p className="text-sm font-sans text-foreground-muted leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-serif tracking-tight mb-6">
            Start Preserving Your Family's Legacy
          </h2>
          <p className="text-lg font-sans mb-10 opacity-90">
            Join families who are keeping their memories and connections alive.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              onClick={() => navigate('/memorial-tribute')}
              data-testid="footer-cta-memorial"
              className="bg-white text-primary hover:bg-gray-100 rounded-full px-8 py-4 font-serif tracking-wide shadow-md transition-all hover:scale-[1.02]"
            >
              <Heart className="w-5 h-5 mr-2" strokeWidth={1.5} />
              Create Memorial Tribute
            </Button>
            <Button 
              onClick={() => navigate('/family-tree')}
              data-testid="footer-cta-tree"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-4 font-serif tracking-wide transition-all hover:scale-[1.02]"
            >
              <TreePine className="w-5 h-5 mr-2" strokeWidth={1.5} />
              Build Family Tree
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-paper border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <TreePine className="w-6 h-6 text-primary" strokeWidth={1.5} />
              <span className="text-xl font-serif font-semibold text-foreground">Sarmoh</span>
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/terms" className="text-sm font-sans text-muted-foreground hover:text-primary transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="text-sm font-sans text-muted-foreground hover:text-primary transition-colors">
                Privacy Policy
              </Link>
            </div>
          </div>
          <div className="text-center pt-6 border-t border-border">
            <p className="text-sm font-sans text-muted-foreground">
              © 2026 TLML Mediatech Pvt Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
